import { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';

import { TFunction } from 'i18next';

interface AnnotationLabel {
  text: string;
  borderColor?: string;
  style?: {
    color?: string;
    background?: string;
  };
}

interface ChartData {
  date_time: string[];
  bait_filling: number[];
  eaten_bait: number[];
  is_added: boolean[];
  added_bait: number[];
  bait_replacement: boolean[];
  annotations?: {
    x: number;
    label: AnnotationLabel;
    strokeDashArray: number;
    borderColor: string;
  }[];
  minMaxSelection: number[];
}

export const combinedChartDataOptions = (
  chartData: ChartData,
  t: TFunction,
  isPercents: boolean,
  showLabels: boolean,
) => {
  const chartOptions: ApexOptions = {
    series: [
      {
        name: t('SBSDetail.chart.value_1'),
        type: 'area',
        data: chartData.bait_filling || [],
        color: '#6A37D4',
      },
      {
        name: t('SBSDetail.chart.value_2'),
        type: 'area',
        data: chartData.eaten_bait || [],
        color: '#f74b4b',
      },
    ],
    chart: {
      id: 'main-chart',
      height: 350,
      type: 'area',
      stacked: false,
      zoom: {
        enabled: true,
        type: 'x',
      },
      toolbar: {
        show: true,
        autoSelected: 'zoom',
        tools: {
          selection: false,
          reset: false,
        },
      },
      selection: {
        enabled: true,
        type: 'x',
      },
    },
    title: {
      text: t('SBSDetail.chart.title'),
      align: 'left',
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#000',
      },
    },
    markers: {
      size: 0,
      shape: 'circle',
    },
    stroke: {
      show: true,
      width: [2, 2],
      curve: 'smooth',
    },
    plotOptions: {
      bar: {
        columnWidth: '100%',
        borderRadius: 1,
        hideZeroBarsWhenGrouped: true,
        borderRadiusApplication: 'around',
      },
    },
    xaxis: {
      type: 'datetime',
      categories: chartData.date_time || [],
      labels: {
        formatter: val => dayjs(val).format('DD-MM-YYYY HH:mm:ss'),
      },
      tooltip: {
        enabled: true,
        formatter: val => dayjs(val).format('DD-MM-YYYY HH:mm:ss'),
      },
      tickPlacement: 'on',
    },
    yaxis: {
      forceNiceScale: true,
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      x: {
        format: 'dd-MM-yyyy HH:mm:ss',
      },
      y: {
        formatter: (value, { seriesIndex }) => {
          if (seriesIndex === 1) {
            return value !== null ? `${value} ${isPercents ? '%' : 'g'}` : '';
          }
          return value !== null ? `${value} ${isPercents ? '%' : 'g'}` : '';
        },
      },
    },
    dataLabels: {
      enabled: showLabels,
      background: {
        enabled: true,
      },
      formatter: (value, { seriesIndex }) => {
        if (seriesIndex === 1) {
          return value !== null ? `${value} ${isPercents ? '%' : 'g'}` : '';
        }
        return value !== null ? `${value} ${isPercents ? '%' : 'g'}` : '';
      },
    },
    annotations: {
      xaxis: chartData.annotations,
    },
  };

  return chartOptions;
};

// Brush chart options
export const brushChartOptions = (chartData: ChartData) => {
  const brushOptions: ApexOptions = {
    series: [
      {
        name: 'Bait quantity',
        type: 'line',
        data: chartData.bait_filling || [],
        color: '#6A37D4',
      },
    ],
    chart: {
      id: 'brush-chart',
      height: 100,
      type: 'line',
      stacked: false,
      zoom: {
        enabled: true,
        type: 'x',
      },
      brush: {
        enabled: true,
        target: 'main-chart',
      },
      selection: {
        enabled: true,
        xaxis: {
          min: chartData.minMaxSelection[0],
          max: chartData.minMaxSelection[1],
        },
      },
    },
    xaxis: {
      categories: chartData.date_time || [],
      offsetY: 1,
      labels: {
        show: false,
        minHeight: 0,
      },
      type: 'datetime',
      tickPlacement: 'on',
    },
    plotOptions: {
      bar: {
        columnWidth: '50%',
        borderRadius: 3,
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
  };

  return brushOptions;
};
